import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export type ProductTone =
  | 'verify'
  | 'shield'
  | 'trust'
  | 'detect'
  | 'stuck'
  | 'connect'
  | 'target'
  | 'neutral';

interface ProductIntroProps {
  /** Leading icon shown in the tinted chip. */
  icon: LucideIcon;
  /** Small uppercase line, e.g. "Authentication · Verify & Verify+". */
  eyebrow: string;
  /** Bold lead sentence — the one-line "what it does". */
  title: string;
  /** Body copy — what the product does, in business language. */
  children: ReactNode;
  /** Accent colour, defaults to neutral (primary). */
  tone?: ProductTone;
  /** Optional short value chips rendered under the body. */
  points?: string[];
  /** Render against a dark page background (Recovery / Connect screens). */
  dark?: boolean;
  /**
   * Stable id for remembering the "dismissed" state. Defaults to a slug of the
   * eyebrow, so the same product's intro shares one dismissal across every page.
   */
  storageKey?: string;
  className?: string;
}

const toneMap: Record<ProductTone, { chip: string; eyebrow: string }> = {
  verify: { chip: 'bg-primary-500/10 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300', eyebrow: 'text-primary-600 dark:text-primary-300' },
  shield: { chip: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300', eyebrow: 'text-emerald-600 dark:text-emerald-300' },
  trust: { chip: 'bg-cyan-500/10 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300', eyebrow: 'text-cyan-600 dark:text-cyan-300' },
  detect: { chip: 'bg-orange-500/10 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300', eyebrow: 'text-orange-600 dark:text-orange-300' },
  stuck: { chip: 'bg-amber-500/10 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300', eyebrow: 'text-amber-600 dark:text-amber-300' },
  connect: { chip: 'bg-sky-500/10 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300', eyebrow: 'text-sky-600 dark:text-sky-300' },
  target: { chip: 'bg-violet-500/10 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300', eyebrow: 'text-violet-600 dark:text-violet-300' },
  neutral: { chip: 'bg-primary-500/10 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300', eyebrow: 'text-primary-600 dark:text-primary-300' },
};

const darkToneEyebrow: Record<ProductTone, string> = {
  verify: 'text-primary-300',
  shield: 'text-emerald-300',
  trust: 'text-cyan-300',
  detect: 'text-orange-300',
  stuck: 'text-amber-300',
  connect: 'text-sky-300',
  target: 'text-violet-300',
  neutral: 'text-primary-300',
};

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const keyFor = (k: string) => `am:product-intro:dismissed:${k}`;

/** Persisted "have I dismissed this intro" flag, remembered in localStorage. */
function useDismissed(key: string): [boolean, (v: boolean) => void] {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(keyFor(key)) === '1';
    } catch {
      return false;
    }
  });
  const update = (v: boolean) => {
    setDismissed(v);
    try {
      if (v) localStorage.setItem(keyFor(key), '1');
      else localStorage.removeItem(keyFor(key));
    } catch {
      /* storage unavailable — keep in-memory state only */
    }
  };
  return [dismissed, update];
}

/**
 * A reusable "what this product does" intro block, written for the customer.
 *
 * It shows once, then the customer can dismiss it — the dismissal is remembered
 * (localStorage), so the intro is skipped everywhere that product appears. When
 * dismissed it collapses to a small pill; pressing that pill (or the expand
 * button) blows the card up to a larger, more prominent version.
 */
export function ProductIntro({
  icon: Icon,
  eyebrow,
  title,
  children,
  tone = 'neutral',
  points,
  dark = false,
  storageKey,
  className,
}: ProductIntroProps) {
  const t = toneMap[tone];
  const [dismissed, setDismissed] = useDismissed(storageKey ?? slug(eyebrow));
  const [expanded, setExpanded] = useState(false);

  // Dismissed and not actively re-opened → the compact "nice" pill button.
  if (dismissed && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label={`Show ${eyebrow} details`}
        title={`Show ${eyebrow} details`}
        className={cn(
          'group inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition',
          dark
            ? 'border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.07] hover:text-white'
            : 'border-border bg-surface text-fg-muted shadow-sm hover:bg-surface-subtle hover:text-fg',
          className,
        )}
      >
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
            dark ? 'bg-white/10 text-white' : t.chip,
          )}
        >
          <Icon className="h-3 w-3" aria-hidden />
        </span>
        <span className="truncate">{eyebrow}</span>
        <Maximize2 className="h-3.5 w-3.5 shrink-0 opacity-60 transition group-hover:opacity-100" aria-hidden />
      </button>
    );
  }

  return (
    <section
      className={cn(
        'relative rounded-2xl border transition-all duration-200',
        expanded ? 'p-6 sm:p-8' : 'p-5',
        dark ? 'border-white/10 bg-white/[0.03]' : 'border-border bg-surface shadow-card',
        expanded && (dark ? 'ring-1 ring-white/15' : 'shadow-lg ring-1 ring-primary-500/15'),
        className,
      )}
    >
      {/* Controls: expand / collapse (bigger) + dismiss (skip from now on). */}
      <div className="absolute right-3 top-3 flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? 'Shrink' : 'Expand'}
          title={expanded ? 'Shrink' : 'Expand'}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg transition',
            dark ? 'text-white/50 hover:bg-white/10 hover:text-white' : 'text-fg-subtle hover:bg-surface-subtle hover:text-fg',
          )}
        >
          {expanded ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
        </button>
        <button
          type="button"
          onClick={() => {
            setExpanded(false);
            setDismissed(true);
          }}
          aria-label="Dismiss — don't show this again"
          title="Dismiss — don't show this again"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg transition',
            dark ? 'text-white/50 hover:bg-white/10 hover:text-white' : 'text-fg-subtle hover:bg-surface-subtle hover:text-fg',
          )}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="flex gap-4 pr-14">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-xl transition-all',
            expanded ? 'h-12 w-12' : 'h-10 w-10',
            dark ? 'bg-white/10 text-white' : t.chip,
          )}
        >
          <Icon className={cn('transition-all', expanded ? 'h-6 w-6' : 'h-5 w-5')} aria-hidden />
        </div>
        <div className="min-w-0 space-y-2">
          <p
            className={cn(
              'font-semibold uppercase tracking-wider',
              expanded ? 'text-xs' : 'text-[11px]',
              dark ? darkToneEyebrow[tone] : t.eyebrow,
            )}
          >
            {eyebrow}
          </p>
          <p
            className={cn(
              'font-semibold',
              expanded ? 'text-base sm:text-lg' : 'text-sm',
              dark ? 'text-white' : 'text-fg',
            )}
          >
            {title}
          </p>
          <div
            className={cn(
              'space-y-2 leading-relaxed',
              expanded ? 'text-[15px]' : 'text-sm',
              dark ? 'text-white/60' : 'text-fg-muted',
            )}
          >
            {children}
          </div>
          {points && points.length > 0 && (
            <ul className="flex flex-wrap gap-2 pt-1">
              {points.map((p) => (
                <li
                  key={p}
                  className={cn(
                    'rounded-full px-2.5 py-1 font-medium ring-1',
                    expanded ? 'text-xs' : 'text-[11px]',
                    dark
                      ? 'bg-white/[0.04] text-white/70 ring-white/10'
                      : 'bg-surface-subtle text-fg-muted ring-border',
                  )}
                >
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
